import { ApiError } from "@/clients/core/ApiError";
import { setIdHeader, setToken } from "@/clients/open_api_injection";
import { UsersService } from "@/clients/services/UsersService";
import { LocalStorageKeys } from "@/constants/local-storage-keys";
import { QueryKeys } from "@/constants/query-keys";
import type { CurrentUser } from "@/hooks/requests/user/use-get-user";
import { UserIdSchema } from "@/schemas/user-schema";
import { markSignedInBefore } from "@/utils/has-signed-in-before";
import { decodeJWT } from "@/utils/jwt";
import { setLocalStorageJson } from "@/utils/local-storage";
// import { logInRevenueCat } from "@/utils/revenue-cat";
import { setUserInfo } from "@/utils/sentry/tools";
import { setMixpanelUser } from "@/utils/mixpanel";
import { setSmartlookUser } from "@/utils/smartlook";
import { useAuth, useClerk, useSignIn, useUser } from "@clerk/clerk-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const useLogin = () => {
  const { isLoaded: isSignInLoaded, signIn, setActive } = useSignIn();
  const { isLoaded: isUserLoaded, user } = useUser();
  const { signOut } = useClerk();
  const queryClient = useQueryClient();

  const isLoaded = isSignInLoaded && isUserLoaded;
  const { getToken } = useAuth();

  const processToken = async (email: string) => {
    const token = await getToken();

    if (!token) {
      throw new Error("No token found");
    }

    // Decode the token to get user info
    let userId = user?.id;

    const decodedToken = decodeJWT(token);
    userId = userId || decodedToken?.sub;
    setToken(token);
    setIdHeader(userId!);
    // await logInRevenueCat(userId!);

    const { data } = await UsersService.loginApiV1UsersLoginPost(userId!);
    const isOnboardingCompleted = data.isOnboardingCompleted;

    const dataStorage = UserIdSchema.parse({
      email,
      isOnboardingCompleted,
      id: userId!,
    });
    setLocalStorageJson(LocalStorageKeys.USER_KEY, dataStorage);
    markSignedInBefore();
    queryClient.setQueryData<CurrentUser>([QueryKeys.USER], (prev) => ({
      ...(prev ?? {}),
      id: userId!,
      isOnboardingCompleted,
    }));
    // Clerk's `setActive` flips `isSignedIn` to true *before* this function
    // writes USER_KEY, which can cause `useGetSubscriptionStatus` to auto-enable
    // and run its queryFn against an empty USER_KEY — the queryFn throws,
    // the query enters error state, and with `retry: false` it never recovers.
    // Force a refetch now that USER_KEY is in place.
    await queryClient.invalidateQueries({
      queryKey: [QueryKeys.SUBSCRIPTION_STATUS],
    });
    // The setQueryData above only seeds a partial user ({ id, isOnboardingCompleted });
    // setQueryData marks it fresh, so on a cold (cache-cleared) login useGetUser would not
    // refetch within staleTime and personalInfo.firstName stays undefined — breaking the
    // name on Profile and AI chat. Invalidate to pull the full user from the server.
    await queryClient.invalidateQueries({
      queryKey: [QueryKeys.USER],
    });
    setUserInfo(dataStorage);
    await setSmartlookUser(userId!, email);
    setMixpanelUser(userId!, email);

    return isOnboardingCompleted;
  };

  const loginMutation = useMutation({
    mutationKey: ["login"],
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      if (!signIn) return;

      const result = await signIn.create({
        identifier: email,
        password,
      });
      if (!result || result.status !== "complete") {
        throw new Error("Failed to sign in");
      }
      const sessionId = signIn.createdSessionId;
      try {
        await setActive({ session: sessionId });
        return await processToken(email);
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          throw new Error("User not found");
        }
        signOut();
        localStorage.removeItem(LocalStorageKeys.ACCESS_TOKEN_KEY);
        console.error("Error setting active session:", error);
      }
    },
  });

  return {
    isLoaded,
    isLoginLoading: loginMutation.isPending,
    isError: loginMutation.isError,
    error: loginMutation.error,
    login: loginMutation.mutate,
    loginAsync: loginMutation.mutateAsync,
    signInStatus: signIn?.status,
  };
};
